import { openAPIClient } from '../openApiClient';
import type {
  CreateCourseServiceConfig,
  CreateCourseStructureServiceConfig,
  DeleteCourseStructureServiceConfig,
  GetAutocourseListServiceConfig,
  GetAutocourseSingleByIdServiceConfig,
} from './types';

/**
 * Gets a paginated list of course structures with optional filtering
 * Used in: CourseStructuresTable for displaying the list of course structures
 */
export async function getCourseStructuresListService({
  httpRequestConfig,
  params,
}: GetAutocourseListServiceConfig) {
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const client = await openAPIClient(httpRequestConfig);
  const response = await client.GET('/v1/auth/autocourse/courseStructure/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Gets a single course structure by ID
 * Used in: AutocourseReviewPage for displaying and editing a specific course structure
 */
export async function getCourseStructureByIdService({
  httpRequestConfig,
  params,
}: GetAutocourseSingleByIdServiceConfig) {
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const client = await openAPIClient(httpRequestConfig);
  const response = await client.GET('/v1/auth/autocourse/courseStructure/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Creates a new course structure from an uploaded document (PDF/Markdown)
 * Used in: AutocourseUploadModal when uploading new documents
 */
export async function createCourseStructureService({
  httpRequestConfig,
  params,
}: CreateCourseStructureServiceConfig) {
  if (params === undefined || params.body === undefined) {
    throw new Error('Params and body are required');
  }

  const client = await openAPIClient(httpRequestConfig);
  const response = await client.POST('/v1/auth/autocourse/courseStructure/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Creates a new course from a course structure
 * Used in: AutocourseReviewPage when finalizing a course structure into a course in the single page
 */
export async function createCourseService({
  httpRequestConfig,
  params,
}: CreateCourseServiceConfig) {
  if (params === undefined || params.body === undefined) {
    throw new Error('Params and body are required');
  }

  const client = await openAPIClient(httpRequestConfig);
  const response = await client.POST('/v1/auth/autocourse/createCourse/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: {
      ...params.body,
      mockLLM: process.env.NEXT_PUBLIC_IS_STAGING === 'true',
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Deletes a course structure
 * DEPRECATED: This should be replaced with createCourseService with a special status
 * as the backend handles deletion through the create course endpoint
 */
export async function deleteCourseStructureByIdService({
  httpRequestConfig,
  params,
}: DeleteCourseStructureServiceConfig) {
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const client = await openAPIClient(httpRequestConfig);
  const response = await client.DELETE('/v1/auth/autocourse/courseStructure/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}
